import { inject, Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';

import { SkillsIdeateHttpClient } from '@core/tellent-skills-data';
import type { SkillsIdeate } from '@core/tellent-skills-types';

import { pollUntilTerminal } from './poll-until-terminal';
import {
  SkillsIdeateEvent,
  SkillsIdeateProgressMessage,
} from './skills-ideate-progress-track.service';

@Injectable({ providedIn: 'root' })
export class SkillsIdeatePollingTrackService {
  private readonly http = inject(SkillsIdeateHttpClient);

  public track(ideateId: number): Observable<SkillsIdeateProgressMessage> {
    return pollUntilTerminal<SkillsIdeate, SkillsIdeateProgressMessage>({
      fetch: () => this.http.getIdeate(ideateId),
      toMessage: data => this.toMessage(data),
      isTerminal: message =>
        message.event === SkillsIdeateEvent.Done || message.event === SkillsIdeateEvent.Failed,
      eventKey: message => message.event,
      // Matches the WS SkillsIdeateProgressTrackService: Failed arrives via
      // observer.next(...) + complete(), so the loading dialog reads the
      // reason from its next: handler.
      onDeadline: () => of({ event: SkillsIdeateEvent.Failed, reason: 'polling_timeout' }),
    });
  }

  private toMessage(data: SkillsIdeate): Observable<SkillsIdeateProgressMessage> {
    switch (data.status) {
      case 'processing':
        return of({ event: SkillsIdeateEvent.Processing });
      case 'completed':
        return of({ event: SkillsIdeateEvent.Done });
      case 'failed':
        return of({
          event: SkillsIdeateEvent.Failed,
          reason: data.errorMessage ?? 'unknown_error',
        });
      default:
        return of({ event: SkillsIdeateEvent.Processing });
    }
  }
}
